import { Stack, useTheme } from "@mui/material";
import { Button, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { AuditLogEntry } from "src/api/client-api/auditLog/auditLogSchemas";
import { ActorBadge } from "./ActorBadge";
import { AuditLogObjectLabel } from "./AuditLogObjectLabel";
import { auditLogScopeLabel, isWithinLastDay } from "./auditLogPresentation";
import { useAuditLogLabels } from "./useAuditLogLabels";

type AuditLogCardListProps = {
    entries: AuditLogEntry[];
    onEntryClick: (entry: AuditLogEntry) => void;
};

export const AuditLogCardList = (props: AuditLogCardListProps) => {
    const { entries, onEntryClick } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const labels = useAuditLogLabels();

    return (
        <Stack gap={theme.spacing(1)} flexGrow={1} minWidth={0}>
            {entries.map((entry) => (
                <Button
                    key={entry.id}
                    variant="text"
                    onClick={() => onEntryClick(entry)}
                    sx={{
                        display: "block",
                        textAlign: "left",
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: theme.spacing(1),
                        padding: theme.spacing(1.5),
                    }}
                >
                    <Stack gap={theme.spacing(1)}>
                        <Stack direction="row" justifyContent="space-between" gap={theme.spacing(1)}>
                            <Stack direction="row" gap={theme.spacing(1)} alignItems="baseline">
                                <Typography variant="small-regular" color="text.secondary">
                                    {labels.utcDay(entry.occurredAt)}
                                </Typography>
                                <Typography variant="small-semibold">{labels.utcTime(entry.occurredAt)}</Typography>
                            </Stack>
                            {isWithinLastDay(entry.occurredAt, new Date()) && (
                                <Typography variant="small-regular" color="text.secondary" flexShrink={0}>
                                    {labels.relativeTime(entry.occurredAt)}
                                </Typography>
                            )}
                        </Stack>
                        <AuditLogObjectLabel entry={entry} />
                        <ActorBadge entry={entry} />
                        <Stack direction="row" gap={theme.spacing(1)} alignItems="baseline">
                            <Typography variant="small-regular" color="text.secondary">
                                {t("settings.audit_log.column.scope", "Scope")}
                            </Typography>
                            <Typography variant="small-regular" noWrap>
                                {auditLogScopeLabel(entry) ?? labels.scopeType(entry.rootType)}
                            </Typography>
                        </Stack>
                    </Stack>
                </Button>
            ))}
        </Stack>
    );
};
